import uniq from "lodash.uniq";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Equalizer from "@/assets/icons/equalizer.svg?react";
import BITable from "@/components/ui/bi/BITable";
import { RowData } from "@/components/ui/bi/BITable/TableRow";
import Button from "@/components/ui/Button";
import { routes } from "@/configs/router.config/routes";
import { useAnalyses } from "@/hooks/useAnalyses";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { cn } from "@/lib/utils";
import { DisplayGroup } from "@/models/displayGroup";
import { VocCountData } from "@/models/vocCount";

interface Props {
  displayGroup: DisplayGroup;
  vocCountData: VocCountData;
  selectedCategoryPath: string;
  hideDataVisibilityToggle: boolean; // 「データを開く/閉じる」ボタンを非表示にするかどうか
  isYearlyView?: boolean; // 年次表示フラグ
  setCurrentStats: (stats: number[]) => void;
  setSelectedCategoryPath: (categoryPath: string) => void;
}

const BITableContainer: React.FC<Props> = (
  {
    displayGroup,
    vocCountData,
    selectedCategoryPath,
    hideDataVisibilityToggle,
    isYearlyView,
    setCurrentStats,
    setSelectedCategoryPath,
  },
) => {
  const navigate = useNavigate();
  const { t } = useTranslation("voiceek_ui");
  const { latestAnalysis } = useAnalyses();
  const { categoryMaxLevel, sessionCurrentWorkspaceId } = useWorkspaces();

  // テーブルを隠しているか
  const [isTableOpen, setIsTableOpen] = useState(true);
  // テーブルをFogで一部隠しているか
  const [isHidingWithFog, setIsHidingWithFog] = useState(!hideDataVisibilityToggle);
  // 展開中の行
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set([]));

  const createBIData = (vocCountData: VocCountData, level = 1): RowData[] => {
    const categories: string[] = uniq(vocCountData.counts.map((vc) => vc.getLayerName(level)));

    return categories.flatMap((category) => {
      const childVocCountData = vocCountData.filterByCategoryName(category);

      return [
        {
          level,
          categoryPath: childVocCountData.currentFilteringPath,
          category,
          stats: isYearlyView ? childVocCountData.yearlyStats : childVocCountData.stats,
          hasLink: level === categoryMaxLevel - 1,
          children: level === categoryMaxLevel - 1 ? [] : createBIData(childVocCountData, level + 1),
        },
      ];
    });
  };

  const biData = vocCountData ? createBIData(vocCountData) : [];
  const isExpandedAll = expandedRows.size === new Set(biData.map((row) => row.categoryPath)).size;

  const handleExpandCollapseButtonClick = () => {
    if (isExpandedAll) {
      setExpandedRows(new Set());
    } else {
      setIsTableOpen(true);
      setIsHidingWithFog(false); // すべて展開ボタンを押した時はFogも非表示にする
      setExpandedRows(new Set(biData.map((row) => row.categoryPath)));
    }
  };

  const handleOpenCloseTableButtonClick = () => {
    if (isTableOpen) {
      // 閉じる場合はバーチャートを全体に戻す
      setCurrentStats(isYearlyView ? vocCountData.yearlyStats : vocCountData.stats);
      setSelectedCategoryPath("/");
      // 展開状態もリセット
      setExpandedRows(new Set());
    }
    setIsTableOpen((prev) => !prev);
  };

  const handleRowClick = (row: RowData) => {
    setSelectedCategoryPath(row.categoryPath);
    setCurrentStats(row.stats);
  };

  const handleAnalysisLinkClick = (row: RowData) => {
    const base = routes.analysis(sessionCurrentWorkspaceId)(displayGroup.id);
    if (!latestAnalysis) return navigate(base);

    // カテゴリは`+`や`&`を含む場合があるためURLエンコードする
    const path = `${base}?analysis=${latestAnalysis.id}&category=${encodeURIComponent(row.categoryPath)}`;
    navigate(path);
  };

  return (
    <>
      <div className={cn("flex justify-end items-center", !isTableOpen && "mb-5")}>
        <div className="flex justify-end mr-5">
          <Button
            onClick={handleExpandCollapseButtonClick}
            variant={isExpandedAll ? "activated" : "secondary"}
            className="px-8"
          >
            <Equalizer className="scale-140 pt-[1px]" />
            {isExpandedAll ? t("bi.dataView.collapseAll") : t("bi.dataView.expandAll")}
          </Button>
        </div>
        <div className="flex justify-end mr-5">
          <Button
            onClick={handleOpenCloseTableButtonClick}
            variant={isTableOpen ? "activated" : "secondary"}
            className="px-8"
          >
            <Equalizer className="scale-140 pt-[1px]" />
            {isTableOpen ? t("bi.dataView.hideDetails") : t("bi.dataView.showDetails")}
          </Button>
        </div>
      </div>

      {isTableOpen && vocCountData && (
        <BITable
          data={biData}
          latestYM={vocCountData.latestYearMonth}
          selectedCategoryPath={selectedCategoryPath}
          expandedRows={expandedRows}
          isHidingWithFog={isHidingWithFog}
          setIsHidingWithFog={setIsHidingWithFog}
          setExpandedRows={setExpandedRows}
          onRowClick={handleRowClick}
          onLinkClick={handleAnalysisLinkClick}
          hideDataVisibilityToggle={hideDataVisibilityToggle}
          isYearlyView={isYearlyView}
        />
      )}
    </>
  );
};

export default BITableContainer;
